// src/pages/gene.codes/GeneticWheel/dashboardPanels/PanelList.tsx
import React, { useMemo, useState } from "react";
import { Panel } from "../../../../types/geneCodes/Panel";
import { Button, Input, List, Tag } from "antd";
import { EyeInvisibleOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { InlineLoading, EmptyState } from "../../../../components/shared";
import { MiniDonut, MiniDonutSegment } from "../../../../components/charts/MiniDonut";
import { usePanelRiskSummary } from "../../../../hooks/genetic/usePanelRiskSummary";
import { buildPanelMiniWheels, buildTraitBreakdown, MiniWheelSlice, panelKey, TraitBreakdown } from "../../../../utils/geneCodes/buildPanelMiniWheels";
import { getNodeColor } from "../../../../utils/geneCodes/wheelColors";
import { isHiddenPanel, panelStatusOf } from "../../../../utils/geneCodes/wheelAuthoring";
import "./dashboardPanels.css";

const MINI_WHEEL_SIZE = 48;
const TRAIT_PIE_SIZE = 30;
const TRAIT_PIE_BORDER = 2;
// Legacy `.infovis-mini { top: 3px }`.
const WHEEL_OFFSET = 3;
const EXPANDED_MAX_HEIGHT = 200;

// The plate keeps its resting surface once the row is tinted, so the wheel still reads as a badge on a selected row.
const PLATE_REST = "var(--mg-bg-subtle)";
const PLATE_SELECTED = "var(--mg-bg-elevated)";

// Slice order is no -> med -> high so the wedge reads best-to-worst clockwise, matching the counts printed beside it.
// The two states after it are not on that axis at all — an uncallable result and an absent one are not verdicts — so they trail it.
// Only the absent one is droppable — see hideUngenotyped.
const RISK_SLICES: { key: keyof Pick<TraitBreakdown, "no" | "med" | "high" | "noCall" | "notGenotyped">; label: string; riskLevel: MiniWheelSlice["riskLevel"] }[] = [
	{ key: "no", label: "No risk", riskLevel: "good" },
	{ key: "med", label: "Med risk", riskLevel: "medium" },
	{ key: "high", label: "High risk", riskLevel: "bad" },
	{ key: "noCall", label: "No call", riskLevel: null },
	{ key: "notGenotyped", label: "Not genotyped", riskLevel: "ungenotyped" },
];

interface PanelListProps {
	panels: Panel[];
	selectedPanelId: number | null;
	onSelectPanel: (panelId: number, sampleId: string | undefined) => void;
	loading?: boolean;
	sampleId?: string;
	selectedTraitName?: string | null;
	onSelectTrait?: (traitName: string) => void;
	// Build-mode only, and OPTIONAL on purpose: WheelModal renders this list on a patient's dashboard,
	// so an edit affordance must be absent there rather than hidden by a role check inside this file.
	onCreateWheel?: () => void;
	showStatus?: boolean;
	// Patient shells hide never-measured markers to match the ring beside this rail; a no-call is a RESULT and stays.
	hideUngenotyped?: boolean;
}

const PanelList: React.FC<PanelListProps> = ({ panels, selectedPanelId, onSelectPanel, loading = false, sampleId, selectedTraitName = null, onSelectTrait, onCreateWheel, showStatus = false, hideUngenotyped = false }) => {
	const [searchTerm, setSearchTerm] = useState("");

	// WheelModal renders PanelList only in the expanded branch, so a collapsed rail unmounts this hook and never fetches.
	const { rows, isLoading: riskLoading } = usePanelRiskSummary(sampleId);
	const wheels = useMemo(() => buildPanelMiniWheels(rows, hideUngenotyped), [rows, hideUngenotyped]);

	// Looked up against the unfiltered list so a search that hides the selected row still resolves its name.
	const selectedPanelName = selectedPanelId === null ? null : panels.find((panel) => panel.id_panels === selectedPanelId)?.name_panel ?? null;
	const traitBreakdown = useMemo(() => (selectedPanelName ? buildTraitBreakdown(rows, selectedPanelName, hideUngenotyped) : []), [rows, selectedPanelName, hideUngenotyped]);
	const visibleSlices = useMemo(() => (hideUngenotyped ? RISK_SLICES.filter((slice) => slice.key !== "notGenotyped") : RISK_SLICES), [hideUngenotyped]);

	const filteredPanels = panels.filter((panel) => {
		const matchesSearch = panel.name_panel.toLowerCase().includes(searchTerm.toLowerCase()) || (panel.desc_short && panel.desc_short.toLowerCase().includes(searchTerm.toLowerCase()));
		return matchesSearch;
	});

	const renderMiniWheel = (panel: Panel, selected: boolean) => {
		const slices = wheels.get(panelKey(panel.name_panel));
		if (riskLoading || !slices || slices.length === 0) {
			// Sized inline from the same constant the donut is drawn at, so the two boxes cannot drift.
			return <div data-testid="panel-mini-wheel-placeholder" style={{ width: MINI_WHEEL_SIZE, height: MINI_WHEEL_SIZE }} />;
		}

		// getNodeColor's hardcoded legacy palette is deliberate: the preview must match the ring it previews, so it reuses the one colour source rather than forking a second.
		const segments: MiniDonutSegment[] = slices.map((slice) => ({
			name: slice.trait,
			value: slice.value,
			color: getNodeColor({ id: `trait-${slice.trait}`, name: slice.trait, type: "trait", value: slice.value, data: {}, riskLevel: slice.riskLevel }, false),
		}));

		return <MiniDonut segments={segments} size={MINI_WHEEL_SIZE} title={panel.name_panel} plate={selected ? PLATE_SELECTED : PLATE_REST} />;
	};

	// The two trailing counts only when they exist, and the last one never on a patient shell, whose ring omits it too.
	const countsText = (breakdown: TraitBreakdown): string =>
		[
			`${breakdown.no} no`,
			`${breakdown.med} med`,
			`${breakdown.high} high`,
			...(breakdown.noCall > 0 ? [`${breakdown.noCall} no call`] : []),
			...(!hideUngenotyped && breakdown.notGenotyped > 0 ? [`${breakdown.notGenotyped} not genotyped`] : []),
		].join(" · ");

	const traitSegments = (breakdown: TraitBreakdown): MiniDonutSegment[] =>
		visibleSlices.map((slice) => ({
			name: slice.label,
			value: breakdown[slice.key],
			tooltip: `${slice.label} — ${breakdown[slice.key]}`,
			color: getNodeColor({ id: `trait-${breakdown.trait}-${slice.key}`, name: breakdown.trait, type: "trait", value: breakdown[slice.key], data: {}, riskLevel: slice.riskLevel }, false),
		}));

	const renderExpanded = (panel: Panel) => {
		if (selectedPanelId !== panel.id_panels) return null;

		return (
			// The row's own click re-selects the panel and refetches it, so the breakdown swallows clicks landing inside it.
			// Keys too: Enter on a trait would otherwise bubble to the row's own Enter handler and refetch the panel.
			<div onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()} data-testid="panel-expanded" className="mg-panel-breakdown">
				<div className="mg-panel-breakdown__meta">
					{panel.modality && panel.modality.trim() ? <span>{panel.modality}</span> : null}
					<span>
						{traitBreakdown.length} {traitBreakdown.length === 1 ? "trait" : "traits"}
					</span>
				</div>

				<div className="mg-panel-breakdown__list" style={{ "--mg-breakdown-max-h": `${EXPANDED_MAX_HEIGHT}px` } as React.CSSProperties}>
					{traitBreakdown.map((breakdown) => {
						const active = selectedTraitName === breakdown.trait;

						return (
							<button
								key={breakdown.trait}
								type="button"
								data-testid="panel-expanded-trait"
								aria-current={active ? "true" : undefined}
								className={active ? "mg-panel-breakdown__row mg-panel-breakdown__row--active" : "mg-panel-breakdown__row"}
								onClick={() => onSelectTrait?.(breakdown.trait)}
							>
								<MiniDonut segments={traitSegments(breakdown)} size={TRAIT_PIE_SIZE} innerRatio={0} borderWidth={TRAIT_PIE_BORDER} />
								<span data-testid="panel-expanded-trait-name" className="mg-panel-breakdown__trait">
									{breakdown.trait}
								</span>
								<span className="mg-panel-breakdown__counts">{countsText(breakdown)}</span>
							</button>
						);
					})}
				</div>

				{panel.red_tag && panel.red_tag.trim() ? <div className="mg-panel-breakdown__tag">suboptimal variants: {panel.red_tag}</div> : null}
			</div>
		);
	};

	return (
		<div className="mg-panel-list" data-testid="panel-list">
			{onCreateWheel ? (
				<div className="mg-panel-list__head">
					<span className="mg-panel-list__title">Wheels</span>
					<span className="mg-panel-list__count">{panels.length}</span>
					<Button type="primary" size="small" icon={<PlusOutlined />} onClick={onCreateWheel} data-testid="panel-list-create">
						New wheel
					</Button>
				</div>
			) : null}

			<div className="mg-panel-list__search">
				<Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search panels…" prefix={<SearchOutlined />} size="small" />
			</div>

			<div className="mg-panel-list__body">
				{loading ? (
				<InlineLoading message="Loading panels…" />
			) : panels.length === 0 && searchTerm === "" ? (
				<EmptyState title="No wheels available for this sample" description="Your practitioner hasn't released any wheels for this sample yet." />
			) : filteredPanels.length === 0 ? (
				<EmptyState title="No panels found" description="Try adjusting your search" />
			) : (
					<List
						dataSource={filteredPanels}
						renderItem={(panel) => {
							const selected = selectedPanelId === panel.id_panels;

							return (
								<List.Item
									role="button"
									tabIndex={0}
									aria-pressed={selected}
									className={selected ? "mg-panel-row mg-panel-row--selected" : "mg-panel-row"}
									onClick={() => onSelectPanel(panel.id_panels, sampleId ? sampleId : undefined)}
									onKeyDown={(event) => {
										if (event.key === "Enter" || event.key === " ") {
											event.preventDefault();
											onSelectPanel(panel.id_panels, sampleId ? sampleId : undefined);
										}
									}}
								>
									{/* The li is a flex row; this column keeps the breakdown below the meta. */}
									<div className="mg-panel-row__inner">
										{/* The wheel is absolutely positioned, so the head reserves its height here or a one-line row collides with it. */}
										<div className="mg-panel-row__head" style={{ "--mg-panel-wheel-h": `${MINI_WHEEL_SIZE + WHEEL_OFFSET * 2}px` } as React.CSSProperties}>
											<div className="mg-panel-row__text">
												<span className="mg-panel-row__name">
											{panel.name_panel}
											{showStatus ? (
												<Tag className={`mg-panel-row__status mg-panel-row__status--${panelStatusOf(panel)}`} icon={isHiddenPanel(panel) ? <EyeInvisibleOutlined /> : undefined} data-testid="panel-row-status">
													{panelStatusOf(panel)}
												</Tag>
											) : null}
										</span>
												{panel.desc_short ? <span className="mg-panel-row__desc">{panel.desc_short}</span> : null}
											</div>
											<div className="mg-panel-row__wheel">{renderMiniWheel(panel, selected)}</div>
										</div>
										{renderExpanded(panel)}
									</div>
								</List.Item>
							);
						}}
					/>
				)}
			</div>

			<div className="mg-panel-list__footer">
				{filteredPanels.length} of {panels.length} panels
			</div>
		</div>
	);
};

export default PanelList;
